import { inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { SubNationalDivisionService } from 'src/app/services/sub-national-division.service';

import { SubNationalDivision } from '../models';
import { AddressForm } from './address-form';
import { CompanyFeeForm } from './company-fee-form';
import { CompanyOfficeForm } from './company-office-form';
import { CompanyPackagePricingForm } from './company-package-pricing-form';
import { ComplianceSettingForm } from './compliance-setting-form';

export class CompanyForm extends FormGroup<
  {
    id: FormControl<number | undefined>;
    legalName: FormControl<string | null>;
    dba: FormControl<string | null>;
    structure: FormControl<string | null>;
    ssn: FormControl<string | null>;
    ein: FormControl<string | null>;
    country: FormControl<'US' | 'CA' | undefined>;
    phone: FormControl<string | null>;
    fax: FormControl<string | null>;
    website: FormControl<string | null>;
    logo: FormControl<string | null>;
    facebook: FormControl<string | null>;
    twitter: FormControl<string | null>;
    linkedIn: FormControl<string | null>;
    status: FormControl<
      'unactivated' | 'reviewed' | 'activated' | 'deactivated' | undefined
    >;
    statusDate: FormControl<Date | null>;
    listApprovedVendorsInPortal: FormControl<boolean>;
    launchWindow: FormControl<number | null>;
    launchStartDate: FormControl<Date | null>;
    allowVendorsToRequestJoin: FormControl<boolean>;
    hasGlobalCompliance: FormControl<boolean>;
    fullServiceOnsite: FormControl<boolean>;
    fullServiceOffsite: FormControl<boolean>;
    premiumServiceOnsite: FormControl<boolean>;
    readyForVive: FormControl<boolean>;
    companyType: FormControl<string | null>;
    registrationStatus: FormControl<
      'unconfirmed' | 'confirmed' | 'complete' | undefined
    >;
    descriptionOfOperations: FormControl<string | null>;
    contractPDF: FormControl<string | null>;
    sampleCert: FormControl<string | null>;
    waiverPolicyUrl: FormControl<string | null>;
    exibitA: FormControl<string | null>;
    contractAdobeSign: FormControl<string | null>;
    vendorSignUpCode: FormControl<string | null>;
    feeWaiverAllowedAmt: FormControl<number | null>;
    ESignProviderId: FormControl<number | undefined>;
    clientPaid: FormControl<boolean>;
    packageType: FormControl<'BASE' | 'FULL' | undefined>;
    contacts: FormControl<
      { type: string; name: string; phone: string; email: string }[] | null
    >;
    clientVendorPrice: FormControl<string | null>;
    vendorPriceNotes: FormControl<string | null>;
    vendorRegistrationPageUrl: FormControl<string | null>;
    acceptsWaivers: FormControl<boolean | null>;
    acceptsAgreementRevisions: FormControl<boolean | null>;
    agreementRevisionNotes: FormControl<string | null>;
    generalClientNotes: FormControl<string | null>;
    usesThirdPartyBillPay: FormControl<boolean | null>;
    thirdPartyBillPayProvider: FormControl<string | null>;
    thirdPartyBillPayNotes: FormControl<string | null>;
    hasVetProfessionals: FormControl<boolean | null>;
    pmtNotes: FormControl<string | null>;
    contractSignDate: FormControl<string | null>;
    kickoffMeetingDate: FormControl<string | null>;
    plannedLaunchDate: FormControl<string | null>;
    actualLaunchDate: FormControl<string | null>;
    plannedCloseDownDate: FormControl<string | null>;
    actualCloseDownDate: FormControl<string | null>;
    originalVendorFileCount: FormControl<number | null>;
    relaunchDate: FormControl<string | null>;
    letterCadence: FormControl<
      '1 Letter Series' | '2 Letter Series' | '3 Letter Series' | 'None' | null
    >;
    letterSender: FormControl<
      'VIVE Sent Letters' | 'Client Sent Letters' | null
    >;
    // operationStates: FormControl<string | null>
    allowExemptVendors: FormControl<boolean>;
    isExemptVendorTypesSetup: FormControl<boolean>;
    lastDateRanAllFileDownload: FormControl<Date | null>;
    allowClientRegisteredVendors: FormControl<boolean>;
    allowAutomatedBusinessVerificationClientRegisteredVendors: FormControl<boolean>;
    usesNewBusinessVerification: FormControl<boolean>;
    verifyClientRegisteredVendorBusinessOnly: FormControl<boolean>;
    creditClientRegisteredVendor: FormControl<boolean>;
    allowTinCheckOnlyFee: FormControl<boolean>;
    useTransporterTrades: FormControl<boolean>;
    allowTollBrothers: FormControl<boolean>;
    isAcceptingNewVendors: FormControl<boolean>;
    globalAssignSites: FormControl<boolean>;
    allowAdditionalInsuredEndorsements: FormControl<boolean>;
    allowClientRegisteredVendorEmails: FormControl<boolean>;
    allowIRSTinMatchFee: FormControl<boolean>;
    allowCorporateFilingReportFee: FormControl<boolean>;

    CompanyPackagePricing: CompanyPackagePricingForm;

    StreetAddress: AddressForm;
    MailingAddress: AddressForm;
  } & {
    ComplianceSettings?: FormArray<ComplianceSettingForm>;
    CompanyFees?: FormArray<CompanyFeeForm>;
    CompanyOffices?: FormArray<CompanyOfficeForm>;

    clientRegisteredVendorStandardFee?: FormControl<number>;
    clientRegisteredVendorIrsTinMatchFee?: FormControl<number>;
    clientRegisteredVendorCorporateFilingReportFee?: FormControl<number>;
  }
> {
  constructor() {
    super({
      id: new FormControl<number | undefined>(
        { value: undefined, disabled: false },
        { nonNullable: true }
      ),
      legalName: new FormControl<string | null>(null, {
        validators: [Validators.required]
      }),
      dba: new FormControl<string | null>(null),
      structure: new FormControl<string | null>(null),
      ssn: new FormControl<string | null>(null),
      ein: new FormControl<string | null>(null),
      country: new FormControl<'US' | 'CA' | undefined>(
        { value: undefined, disabled: false },
        { nonNullable: true, validators: [Validators.required] }
      ),
      phone: new FormControl<string | null>(null),
      fax: new FormControl<string | null>(null),
      website: new FormControl<string | null>(null),
      logo: new FormControl<string | null>(null),
      facebook: new FormControl<string | null>(null),
      twitter: new FormControl<string | null>(null),
      linkedIn: new FormControl<string | null>(null),
      status: new FormControl<
        'unactivated' | 'reviewed' | 'activated' | 'deactivated' | undefined
      >(
        { value: undefined, disabled: false },
        { nonNullable: true, validators: [Validators.required] }
      ),
      statusDate: new FormControl<Date | null>(null),
      listApprovedVendorsInPortal: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      launchWindow: new FormControl<number | null>(null),
      launchStartDate: new FormControl<Date | null>(null),
      allowVendorsToRequestJoin: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      hasGlobalCompliance: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      fullServiceOnsite: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      fullServiceOffsite: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      premiumServiceOnsite: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      readyForVive: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      companyType: new FormControl<string | null>(null, {
        validators: [Validators.required]
      }),
      registrationStatus: new FormControl<
        'unconfirmed' | 'confirmed' | 'complete' | undefined
      >(
        { value: undefined, disabled: false },
        { nonNullable: true, validators: [Validators.required] }
      ),
      descriptionOfOperations: new FormControl<string | null>(null),
      contractPDF: new FormControl<string | null>(null),
      sampleCert: new FormControl<string | null>(null),
      waiverPolicyUrl: new FormControl<string | null>(null),
      exibitA: new FormControl<string | null>(null),
      contractAdobeSign: new FormControl<string | null>(null),
      vendorSignUpCode: new FormControl<string | null>(null),
      feeWaiverAllowedAmt: new FormControl<number | null>(null),
      ESignProviderId: new FormControl<number | undefined>(
        { value: undefined, disabled: false },
        { nonNullable: true, validators: [Validators.required] }
      ),
      clientPaid: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      packageType: new FormControl<'BASE' | 'FULL' | undefined>(
        { value: undefined, disabled: false },
        { nonNullable: true }
      ),
      contacts: new FormControl<
        { type: string; name: string; phone: string; email: string }[] | null
      >(null),
      clientVendorPrice: new FormControl<string | null>(null),
      vendorPriceNotes: new FormControl<string | null>(null),
      vendorRegistrationPageUrl: new FormControl<string | null>(null),
      acceptsWaivers: new FormControl<boolean | null>(null),
      acceptsAgreementRevisions: new FormControl<boolean | null>(null),
      agreementRevisionNotes: new FormControl<string | null>(null),
      generalClientNotes: new FormControl<string | null>(null),
      usesThirdPartyBillPay: new FormControl<boolean | null>(null),
      thirdPartyBillPayProvider: new FormControl<string | null>(null),
      thirdPartyBillPayNotes: new FormControl<string | null>(null),
      hasVetProfessionals: new FormControl<boolean | null>(null),
      pmtNotes: new FormControl<string | null>(null),
      contractSignDate: new FormControl<string | null>(null),
      kickoffMeetingDate: new FormControl<string | null>(null),
      plannedLaunchDate: new FormControl<string | null>(null),
      actualLaunchDate: new FormControl<string | null>(null),
      plannedCloseDownDate: new FormControl<string | null>(null),
      actualCloseDownDate: new FormControl<string | null>(null),
      originalVendorFileCount: new FormControl<number | null>(null),
      relaunchDate: new FormControl<string | null>(null),
      letterCadence: new FormControl<
        | '1 Letter Series'
        | '2 Letter Series'
        | '3 Letter Series'
        | 'None'
        | null
      >(null),
      letterSender: new FormControl<
        'VIVE Sent Letters' | 'Client Sent Letters' | null
      >(null),
      // operationStates: new FormControl<string | null>(null),
      allowExemptVendors: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      isExemptVendorTypesSetup: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      lastDateRanAllFileDownload: new FormControl<Date | null>(null),
      allowClientRegisteredVendors: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      allowAutomatedBusinessVerificationClientRegisteredVendors:
        new FormControl<boolean>(false, {
          nonNullable: true,
          validators: [Validators.required]
        }),
      usesNewBusinessVerification: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      verifyClientRegisteredVendorBusinessOnly: new FormControl<boolean>(
        false,
        { nonNullable: true, validators: [Validators.required] }
      ),
      creditClientRegisteredVendor: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      allowTinCheckOnlyFee: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      useTransporterTrades: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      allowTollBrothers: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      isAcceptingNewVendors: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      globalAssignSites: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      allowAdditionalInsuredEndorsements: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      allowClientRegisteredVendorEmails: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      allowIRSTinMatchFee: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      allowCorporateFilingReportFee: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),

      CompanyPackagePricing: new CompanyPackagePricingForm(),

      StreetAddress: new AddressForm(),
      MailingAddress: new AddressForm()
    });
  }
}

// Need to implement some of the forms below.
/*
ApConfigs?: ApConfig[];
CompanyFees?: CompanyFee[];
CompanyVendor?: CompanyVendor[];
ComplianceSettings?: ComplianceSetting[];
BusinessComplianceSettings?: BusinessComplianceSetting[];
RegistrationProgresses?: RegistrationProgress[];
Addresses?: Address[];
StreetAddress?: Address;
MailingAddress?: Address;
Contacts?: Contact[];
CompanyComplianceTypes?: CompanyComplianceType[];
CompanyOperationStates?: CompanyOperationState[] = [];
CompanyPackagePricing?: CompanyPackagePricing;
CompanyServiceRankings?: CompanyServiceRanking[]
Sites?: Site[];
*/
